import { useState } from 'react'
import { Plus, Edit2, Trash2, Eye, EyeOff, Pin, Megaphone } from 'lucide-react'
import { useCollection } from '../../hooks/useFirestore'
import { formatRelativeTime, truncate } from '../../utils/helpers'
import { Button, Modal, Input, Textarea, Badge, Checkbox } from '../../components/common'

const AnnouncementForm = ({ announcement, departments, onSave, onClose }) => {
  const [formData, setFormData] = useState(announcement || {
    title: '',
    body: '',
    departmentIds: [],
    pinned: false,
    isPublished: true,
  })
  const [saving, setSaving] = useState(false)

  const toggleDept = (slug) => {
    const next = formData.departmentIds.includes(slug)
      ? formData.departmentIds.filter((s) => s !== slug)
      : [...formData.departmentIds, slug]
    setFormData({ ...formData, departmentIds: next })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    await onSave(formData)
    setSaving(false)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Input
        label="Title"
        required
        value={formData.title}
        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        placeholder="Rehearsal moved to Saturday"
      />

      <Textarea
        label="Announcement (markdown supported)"
        rows={5}
        required
        value={formData.body}
        onChange={(e) => setFormData({ ...formData, body: e.target.value })}
        placeholder="Details for your workers..."
      />

      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">
          Visible to (none selected = all workers)
        </p>
        <div className="flex flex-wrap gap-4">
          {departments.map((dept) => (
            <Checkbox
              key={dept.id}
              label={dept.name}
              checked={formData.departmentIds.includes(dept.id)}
              onChange={() => toggleDept(dept.id)}
            />
          ))}
        </div>
      </div>

      <div className="flex items-center gap-6">
        <Checkbox
          label="Pinned to top"
          checked={formData.pinned}
          onChange={(e) => setFormData({ ...formData, pinned: e.target.checked })}
        />
        <Checkbox
          label="Published"
          checked={formData.isPublished}
          onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
        />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={saving}>
          {announcement ? 'Update Announcement' : 'Post Announcement'}
        </Button>
      </div>
    </form>
  )
}

const AnnouncementsManager = () => {
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const { data: announcements, add, update, remove } = useCollection('announcements')
  const { data: departments } = useCollection('departments', {
    orderByField: 'order',
    orderDirection: 'asc',
  })

  const deptName = (slug) => departments?.find((d) => d.id === slug)?.name || slug

  const handleSave = async (data) => {
    const payload = {
      ...data,
      publishedAt: data.isPublished
        ? (editing?.publishedAt || new Date().toISOString())
        : null,
    }
    if (editing?.id) {
      await update(editing.id, payload)
    } else {
      await add(payload)
    }
    setShowModal(false)
    setEditing(null)
  }

  const handleDelete = async () => {
    if (deleteConfirm) {
      await remove(deleteConfirm)
      setDeleteConfirm(null)
    }
  }

  const togglePublished = async (a) => {
    await update(a.id, {
      isPublished: !a.isPublished,
      publishedAt: !a.isPublished ? (a.publishedAt || new Date().toISOString()) : a.publishedAt,
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-dark-100">Announcements</h1>
          <p className="text-gray-500">Updates shown on the workers portal</p>
        </div>
        <Button
          variant="primary"
          icon={Plus}
          onClick={() => {
            setEditing(null)
            setShowModal(true)
          }}
        >
          New Announcement
        </Button>
      </div>

      <div className="space-y-3">
        {(announcements || []).map((a) => (
          <div key={a.id} className="bg-white rounded-xl shadow-lg p-5 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                {a.pinned && <Pin className="w-4 h-4 text-accent-600" />}
                <p className="font-medium text-dark-100">{a.title}</p>
                <Badge variant={a.isPublished ? 'success' : 'gray'} size="sm">
                  {a.isPublished ? 'Published' : 'Draft'}
                </Badge>
                {(a.departmentIds || []).map((slug) => (
                  <Badge key={slug} variant="primary" size="sm">{deptName(slug)}</Badge>
                ))}
                {(a.departmentIds || []).length === 0 && (
                  <Badge variant="gray" size="sm">All workers</Badge>
                )}
              </div>
              <p className="text-sm text-gray-600 mt-1">{truncate(a.body, 140)}</p>
              {a.createdAt && (
                <p className="text-xs text-gray-400 mt-1">
                  {formatRelativeTime(a.createdAt?.toDate ? a.createdAt.toDate() : a.createdAt)}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => togglePublished(a)}
                className={`p-2 rounded-lg transition-colors ${
                  a.isPublished
                    ? 'text-emerald-600 hover:bg-emerald-50'
                    : 'text-gray-400 hover:bg-gray-100'
                }`}
              >
                {a.isPublished ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
              </button>
              <button
                onClick={() => {
                  setEditing(a)
                  setShowModal(true)
                }}
                className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
              >
                <Edit2 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setDeleteConfirm(a.id)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        ))}

        {(announcements || []).length === 0 && (
          <div className="bg-white rounded-xl shadow-lg text-center py-12 text-gray-500">
            <Megaphone className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No announcements yet</p>
          </div>
        )}
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setEditing(null)
        }}
        title={editing ? 'Edit Announcement' : 'New Announcement'}
        size="lg"
      >
        <AnnouncementForm
          announcement={editing}
          departments={departments || []}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false)
            setEditing(null)
          }}
        />
      </Modal>

      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Announcement"
        size="sm"
      >
        <p className="text-gray-600 mb-6">Delete this announcement?</p>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default AnnouncementsManager
